/* eslint-disable react-hooks/exhaustive-deps */
import isEqual from 'lodash/isEqual';
import { Box, Flex } from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormHeaderFieldList,
  FormList,
  TitleLabel,
} from 'src/components/Common/FormComponent';
import { generalExport } from 'src/pages/Policy/utils/fns';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import {
  asApprovedHeaderListSelector,
  asBlockedHeaderListSelector,
  asApprovedSenderEmailEntriesSelector,
  asBlockedSenderEmailEntriesSelector,
} from '@Policy/store/filters/advancedSpamProtection';
import ImportModal from '../../Components/ImportModal';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';

export const ApprovedSenderList = () => {
  // get store from context
  const contextStore = usePolicyContext();
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  const { t } = useTranslation();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get state and action from store
  const asApprovedSenderEmailEnabled = policySelector(
    (s) => s.tmAntispamfilters.asApprovedSenderEmailEnabled
  );
  const approvedSenderList = contextSelector(
    asApprovedSenderEmailEntriesSelector,
    isEqual
  );
  const approvedListMaxCount = contextSelector(
    (s) => s.config.limitation.asApprovedSenderMaxCount
  );
  const setAsState = contextSelector((s) => s.setAsState);
  const addAsApprovedSender = contextSelector((s) => s.addAsApprovedSender);
  const removeAsApprovedSender = contextSelector(
    (s) => s.removeAsApprovedSender
  );
  const importAsApprovedSender = contextSelector(
    (s) => s.importAsApprovedSender
  );
  // local state
  const handleAsApprovedSenderEmailEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAsState({ asApprovedSenderEmailEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  // approved sender export
  const handleApprovedSenderExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'approved-sender-list',
    });
  }, []);
  // approved sender import
  const [
    isApprovedSenderImportModalVisible,
    setIsApprovedSenderImportModalVisible,
  ] = React.useState(false);
  const handleApprovedSenderImportModalClose = React.useCallback(() => {
    setIsApprovedSenderImportModalVisible(false);
  }, []);
  const handleApprovedSenderImportModalOpen = React.useCallback(() => {
    setIsApprovedSenderImportModalVisible(true);
  }, []);
  const handleApprovedSenderImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    importAsApprovedSender(entries as string[]);
    setIsApprovedSenderImportModalVisible(false);
  }, []);
  useWhyDidYouUpdate('Antispam ApprovedSenderList', {
    asApprovedSenderEmailEnabled,
    approvedSenderList,
    isApprovedSenderImportModalVisible,
  });
  return (
    <>
      <FormList
        onAdd={addAsApprovedSender}
        onDelete={removeAsApprovedSender}
        onExport={handleApprovedSenderExport}
        onImport={handleApprovedSenderImportModalOpen}
        list={approvedSenderList}
        limit={approvedListMaxCount}
        status={!!asApprovedSenderEmailEnabled}
        setStatus={handleAsApprovedSenderEmailEnabledChange}
        label={t('policy.approvedSenderList.title')}
        description={t('policy.approvedSenderList.supportWildcardDesc')}
      />
      <ImportModal
        isVisible={isApprovedSenderImportModalVisible}
        onClose={handleApprovedSenderImportModalClose}
        onImport={handleApprovedSenderImport}
      />
    </>
  );
};

export const BlockedSenderList = () => {
  // get store from context
  const contextStore = usePolicyContext();
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  const { t } = useTranslation();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  // get state and action from store
  const asBlockedSenderEmailEnabled = policySelector(
    (s) => s.tmAntispamfilters.asBlockedSenderEmailEnabled
  );
  const blockedSenderList = contextSelector(
    asBlockedSenderEmailEntriesSelector,
    isEqual
  );
  const blockedListMaxCount = contextSelector(
    (s) => s.config.limitation.blockedListMaxCount
  );
  const setAsState = contextSelector((s) => s.setAsState);
  const addAsBlockedSender = contextSelector((s) => s.addAsBlockedSender);
  const removeAsBlockedSender = contextSelector((s) => s.removeAsBlockedSender);
  const importAsBlockedSender = contextSelector((s) => s.importAsBlockedSender);
  // local state
  const handleAsBlockedSenderEmailEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAsState({ asBlockedSenderEmailEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  // blocked sender export
  const onBlockedSenderExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'blocked-sender-list',
    });
  }, []);
  // blocked sender import
  const [
    isBlockedSenderImportModalVisible,
    setIsBlockedSenderImportModalVisible,
  ] = React.useState(false);
  const handleBlockedSenderImportModalClose = React.useCallback(() => {
    setIsBlockedSenderImportModalVisible(false);
  }, []);
  const handleBlockedSenderImportModalOpen = React.useCallback(() => {
    setIsBlockedSenderImportModalVisible(true);
  }, []);
  const handleBlockedSenderImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    importAsBlockedSender(entries as string[]);
    setIsBlockedSenderImportModalVisible(false);
  }, []);
  useWhyDidYouUpdate('Antispam BlockedSenderList', {
    asBlockedSenderEmailEnabled,
    blockedSenderList,
    isBlockedSenderImportModalVisible,
  });
  return (
    <>
      <FormList
        onAdd={addAsBlockedSender}
        onDelete={removeAsBlockedSender}
        onExport={onBlockedSenderExport}
        onImport={handleBlockedSenderImportModalOpen}
        list={blockedSenderList}
        limit={blockedListMaxCount}
        status={!!asBlockedSenderEmailEnabled}
        setStatus={handleAsBlockedSenderEmailEnabledChange}
        label={t('policy.blockSenderList.title')}
        description={t('policy.approvedSenderList.supportWildcardDesc')}
      />
      <ImportModal
        isVisible={isBlockedSenderImportModalVisible}
        onClose={handleBlockedSenderImportModalClose}
        onImport={handleBlockedSenderImport}
      />
    </>
  );
};
export const ApprovedHeaderFields = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  const contextSelector = storeSelector(contextStore);
  // get state and action from store
  const asApprovedHeaderEnabled = policySelector(
    (s) => s.tmAntispamfilters.asApprovedHeaderEnabled
  );

  const approvedHeaderList = contextSelector(
    asApprovedHeaderListSelector,
    isEqual
  );

  const approvedHeaderMaxNumber = contextSelector(
    (s) => s.config.limitation.approvedHeaderMaxNumber
  );
  const approvedHeaderMaxLength = contextSelector(
    (s) => s.config.limitation.approvedHeaderMaxLength
  );
  const setAsState = contextSelector((s) => s.setAsState);

  const addAsApprovedHeader = contextSelector((s) => s.addAsApprovedHeader);
  const removeAsApprovedHeader = contextSelector(
    (s) => s.removeAsApprovedHeader
  );
  // local state

  const handleAsApprovedHeaderEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAsState({ asApprovedHeaderEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );

  const operators = React.useMemo(() => {
    return [
      { value: 'equals', name: t('common.label.equals') },
      { value: 'contains', name: t('common.label.contains') },
    ];
  }, []);
  useWhyDidYouUpdate('Antispam approvedHeaderList', {
    approvedHeaderList,
    asApprovedHeaderEnabled,
  });
  return (
    <FormHeaderFieldList
      onAdd={addAsApprovedHeader}
      onDelete={removeAsApprovedHeader}
      list={approvedHeaderList}
      limit={approvedHeaderMaxNumber}
      headerMaxLength={approvedHeaderMaxLength}
      status={!!asApprovedHeaderEnabled}
      setStatus={handleAsApprovedHeaderEnabledChange}
      label={t('policy.headerFields.desc')}
      operators={operators}
    />
  );
};

export const BlockedHeaderFields = () => {
  const { t } = useTranslation();
  const contextStore = usePolicyContext();
  // get store selector
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  const contextSelector = storeSelector(contextStore);
  // get state and action from store
  const asBlockedHeaderEnabled = policySelector(
    (s) => s.tmAntispamfilters.asBlockedHeaderEnabled
  );
  const setAsState = contextSelector((s) => s.setAsState);
  const addAsBlockedHeader = contextSelector((s) => s.addAsBlockedHeader);
  const removeAsBlockedHeader = contextSelector((s) => s.removeAsBlockedHeader);
  const handleAsBlockedHeaderEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAsState({ asBlockedHeaderEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  const blockedHeaderList = contextSelector(
    asBlockedHeaderListSelector,
    isEqual
  );
  const blockedHeaderMaxNumber = contextSelector(
    (s) => s.config.limitation.blockedHeaderMaxNumber
  );
  const blockedHeaderMaxLength = contextSelector(
    (s) => s.config.limitation.blockedHeaderMaxLength
  );

  const operators = React.useMemo(() => {
    return [
      { value: 'equals', name: t('common.label.equals') },
      { value: 'contains', name: t('common.label.contains') },
      { value: 'not equals', name: t('common.label.notEquals') },
      { value: 'not contains', name: t('common.label.notContains') },
    ];
  }, [t]);

  useWhyDidYouUpdate('Antispam blockedHeaderList', {
    blockedHeaderList,
    asBlockedHeaderEnabled,
  });
  return (
    <FormHeaderFieldList
      onAdd={addAsBlockedHeader}
      onDelete={removeAsBlockedHeader}
      list={blockedHeaderList}
      limit={blockedHeaderMaxNumber}
      headerMaxLength={blockedHeaderMaxLength}
      status={!!asBlockedHeaderEnabled}
      setStatus={handleAsBlockedHeaderEnabledChange}
      label={t('policy.blockedHeaderFields.label')}
      operators={operators}
      isBlockedHeaderFieldList={true}
    />
  );
};

function ApproveBlockList({
  approvedHeaderFieldsShow = true,
}: {
  approvedHeaderFieldsShow?: boolean;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <Box ml="1px" mt="34px">
        <Flex direction="column">
          <TitleLabel title={t('policy.title.senders')} />
          <ApprovedSenderList />
          <BlockedSenderList />
          <>
            <TitleLabel title={t('policy.headerFields.title')} />
            {approvedHeaderFieldsShow ? (
              <>
                <ApprovedHeaderFields />
              </>
            ) : null}
            <BlockedHeaderFields />
          </>
        </Flex>
      </Box>
    </div>
  );
}

export default React.memo(ApproveBlockList);
